import ImageCaptureStore from "../stores/ImageCaptureStore";    
const VPID_NOT_FOUND_ERROR = "Vision Program ID not found";
const PHOTO_CAPTURE_FAILED_ERROR = "Failed to capture image from Snappi";

export const captureImageSnappi = async (
    visionProgramId: string,
    shouldCaptureUnblurred: boolean,
    imageCaptureStore: ImageCaptureStore
): Promise<void> => {
    const vpid = Number(visionProgramId);

    if (Number.isNaN(vpid)) {
        imageCaptureStore.setError("Invalid Vision Program ID");
        return;
    }

    imageCaptureStore.setLoadingCapture(true);

    try {
        const takePhotoUrl = `https://snappi.vivacitylabs.com/snappi/${vpid}?blur=${!shouldCaptureUnblurred}`;

        const response = await fetch(takePhotoUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${imageCaptureStore.jwt}`,
            },
        });

        if (response.status === 404) {
            imageCaptureStore.setError(VPID_NOT_FOUND_ERROR);
            console.warn(VPID_NOT_FOUND_ERROR);
            return;
        }

        if (!response.ok) {
            const errMsg = `${PHOTO_CAPTURE_FAILED_ERROR}: ${response.status} ${response.statusText}`;
            imageCaptureStore.setError(errMsg);
            console.error(errMsg);
            return;
        }

        const arrayBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        imageCaptureStore.setImageBytes(bytes);
    } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        imageCaptureStore.setError(message);
        console.error("Snapshot error:", message);
    } finally {
        imageCaptureStore.setLoadingCapture(false);
    }
};

export const persistCapturedImage = async (
    visionProgramId: string,
    imageCaptureStore: ImageCaptureStore
): Promise<void> => {
    const vpid = Number(visionProgramId);

    if (Number.isNaN(vpid)) {
        imageCaptureStore.setError("Invalid Vision Program ID");
        throw new Error("Invalid Vision Program ID");
    }

    if (!imageCaptureStore.imageBytes) {
        imageCaptureStore.setError("No image to persist");
        throw new Error("No image to persist");
    }

    imageCaptureStore.setLoadingSave(true);

    try {
        const persistPhotoUrl = `https://snappi.vivacitylabs.com/upload_reference_image/${vpid}.jpeg?png_or_jpeg=jpeg`;

        const bytes = new Uint8Array(imageCaptureStore.imageBytes);

        const response = await fetch(persistPhotoUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${imageCaptureStore.jwt}`,
                "Content-Type": "application/octet-stream",
            },
            body: bytes,
        });

        if (!response.ok) {
            const errMsg = `Failed to save image: ${response.status} ${response.statusText}`;
            imageCaptureStore.setError(errMsg);
            console.error(errMsg);
            throw new Error("Failed to save image");
        }

        imageCaptureStore.setError(null); // Clear any previous errors
        console.log("Image saved successfully");
    } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        imageCaptureStore.setError(message);
        console.error("Save image error:", message);
        throw error; // Re-throw the error so the caller can handle it
    } finally {
        imageCaptureStore.setLoadingSave(false);
    }
}